import styled from '@emotion/styled'
import {
  useShop,
  useModal,
  useConsumerCart,
  useQueryParams,
  useCartMatch,
  useImages,
  useCreateDefaultCart
} from 'shop/hooks'
import SoldOutOverlay from './SoldOutOverlay'
import { IoAddOutline as AddIcon } from 'react-icons/io5'
import { HiCheck as CheckIcon } from 'react-icons/hi'
import { AiOutlineLoading3Quarters as LoadingIcon } from 'react-icons/ai'
import { CSSObject, keyframes } from '@emotion/react'
import { DietaryRequirements } from '../ProductInfo'

import {
  TrackableEvent,
  trackUserActionsFBPixel,
  trackUserActionsGA4
} from 'tracker'

import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  merchantGA4EcommTrackAddToCart,
  slerpGA4EcommTrackAddToCart
} from 'tracker/GA/ecommerce'
import { CategoryProduct } from '../Shop/Categories'
import { Medium } from '../common'
import { StyledHTMLElement } from 'shop/theme/types'
import { fadeIn, LineBreak } from '../Shop/commonStyles'
import { getCurrentCartId, SimpleFormat } from 'shop/utils'
import { LineBreakWrapper } from './commonStyles'
import { FulfillmentType, ImageTagType } from 'shop/types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import RouterAnchor from '../RouterAnchor/RouterAnchor'
import PromoTag from './PromoTag'
import { OrderItemSource } from 'shop/types/cart'

const AddToCartButton = ({
  onClick,
  buttonState
}: {
  onClick: (e: SyntheticEvent) => void
  buttonState: ProductCardButtonState
}) => (
  <ButtonWrapper
    onClick={(e: SyntheticEvent) => {
      e.stopPropagation()
      onClick(e)
    }}
    disabled={buttonState === 'loading' || buttonState === 'done'}
    data-testid='add-to-cart-button'
  >
    {getIcon(buttonState)}
  </ButtonWrapper>
)

const getIcon = (buttonState?: string | null) => {
  if (buttonState === 'loading') return <Loading size={20} />
  if (buttonState === 'done') return <CheckIcon color='green' size={20} />
  return <AddIcon size={20} />
}

export interface ProductCardProps {
  product: CategoryProduct
  productCategory: string
  isLast: boolean
  isStoreAcceptingOrders?: boolean
  source: OrderItemSource
  contentDimensions?: string
  showDescription?: boolean
}

type ProductCardButtonState = 'loading' | 'done' | null

const ProductCard = ({
  product,
  productCategory,
  isLast,
  isStoreAcceptingOrders = true,
  source,
  contentDimensions,
  showDescription = true
}: ProductCardProps) => {
  const {
    id: productId,
    defaultVariantId,
    images,
    inStock,
    name: productName,
    pricing,
    quickAddAllowed,
    description,
    dietaryRequirements,
    slug: productSlug
  } = product
  const { maximum: maxPrice, minimum: minPrice } = pricing
  const params = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [titleLines, setTitleLines] = useState(1)
  const [isLoaded, setIsLoaded] = useState(false)
  const titleRef = useRef<HTMLDivElement>(null)
  const imageUrl = images[0]?.standard

  const { isModalOpen, cartSwitchModal, openModal } = useModal()
  const { cart, cartLoading, createOrderItemConsumerCart } = useConsumerCart()
  const { partner, config, currentPartnerStore } = useShop()
  const { getImage, handleImageError } = useImages(
    [{ imageUrl }],
    ImageTagType.Standard
  )

  const cartId = getCurrentCartId(config.domain)
  const [buttonState, setButtonState] = useState<ProductCardButtonState>(null)
  const [shouldShowPromoTag, setShouldShowPromoTag] = useState(false)
  const { getQueryParam } = useQueryParams()
  const { isCartMatch } = useCartMatch()

  const queryParamFulfillmentType = getQueryParam(
    'fulfillment_type'
  ).toUpperCase() as FulfillmentType

  // prioritise partner store data and fallback to URL slug
  const storeSlug = currentPartnerStore?.slug || params.slug
  // encode the productSlug as products may come with special characters
  const productPath = `/store/${storeSlug}/${encodeURIComponent(productSlug)}` // for navigation
  const productHref = `/order${productPath}` // for href display
  const productLinkState = { product: { ...product }, productCategory }

  /** Update promo tag visibility on discount application/removal */
  useEffect(() => {
    // If no cart, show promo tag if product is in stock & has promotion
    if (!cart?.id) {
      const shouldShowPromo = !!(inStock && product.promotion)
      setShouldShowPromoTag(shouldShowPromo)
      return
    }
    // If we have a cart, show promo tag only when product is in stock, has promotion & we have discount code applied
    const shouldShowPromo = !!(
      inStock &&
      product.promotion &&
      cart?.summary.discount?.code
    )
    setShouldShowPromoTag(shouldShowPromo)
  }, [cart?.id, cart?.summary.discount?.code, inStock, product.promotion])

  /** If no cart open modal, else route to product card */
  const handleProductClick = (e: SyntheticEvent) => {
    e.preventDefault()
    navigate(
      {
        pathname: productPath,
        search: location.search
      },
      {
        state: productLinkState
      }
    )
  }

  const ecommTrackAddToCartCallback = (params: {
    storeName: string
    partnerName: string
  }) => {
    // eccommerce tracking
    const trackParams = {
      currency: 'gbp',
      value: parseFloat(minPrice.discounted || minPrice.base), // quick-add will always be discounted/base price
      items: [
        {
          item_id: productId,
          item_name: productName,
          affiliation: params.storeName,
          item_brand: params.partnerName,
          item_variant: productName, // quick-add will always be default name
          price: parseFloat(minPrice.base), // quick-add will always be base price
          quantity: 1 // quick-add will only add 1
        }
      ]
    }
    slerpGA4EcommTrackAddToCart(trackParams)
    merchantGA4EcommTrackAddToCart(trackParams)
  }

  /** Quick Add Item to Cart */
  const addItemToCart = () => {
    setButtonState('loading')

    const payload = {
      variantId: defaultVariantId,
      quantity: 1,
      source: source
    }

    createOrderItemConsumerCart(payload)
      .then(() => {
        ecommTrackAddToCartCallback({
          storeName: cart?.store.name || '',
          partnerName: partner?.name || ''
        })
        setButtonState('done')
        setTimeout(() => setButtonState(null), 400)
      })
      .catch((err) => {
        console.error('ADD TO CART ERROR: ', err)
        setButtonState(null)
      })

    const trackingBody = {
      category: productCategory ? productCategory : '',
      action: TrackableEvent.ProductAdded,
      label: productName,
      value: 1
    }

    trackUserActionsGA4(trackingBody, 'slerpGA4Tracking')

    // legacy tracking
    trackUserActionsFBPixel('AddToCart', {
      content_name: productName,
      content_type: 'product',
      contents: [{ id: productId, quantity: 1 }]
    })
    trackUserActionsGA4(trackingBody, 'merchantGA4Tracking')
  }

  const {
    handleCreateDefaultCart,
    setShowCartSwitchModal,
    showCartSwitchModal
  } = useCreateDefaultCart(
    currentPartnerStore,
    queryParamFulfillmentType,
    addItemToCart
  )

  const handleAddToCart = (event: SyntheticEvent) => {
    event.preventDefault()

    if (isCartMatch) {
      // Prevents quick add if cart is loading, i.e. if another createOrderItem is in progress
      if (cartLoading) return

      if (cartId) return addItemToCart()
    }

    if (!cartId) {
      handleCreateDefaultCart()
      return
    }

    if (!isCartMatch && cartId) {
      // Only open cartSwitchModal if there are items in the cart
      if (cart?.orderItems.length) {
        openModal('cartSwitch')
        setShowCartSwitchModal(true)
        return
      } else {
        handleCreateDefaultCart()
      }
    }
  }

  const showFromPrefix =
    !quickAddAllowed &&
    inStock &&
    (!!(minPrice.discounted && maxPrice.discounted)
      ? parseFloat(maxPrice.discounted) > parseFloat(minPrice.discounted)
      : parseFloat(maxPrice.base) > parseFloat(minPrice.base))

  const memoizedPrice = useMemo(() => {
    const pricePrefix = showFromPrefix ? 'From' : ''
    const basePrice = minPrice.base
    const discountedPrice = minPrice.discounted
    const hasCartDiscount = cart?.summary.discount?.code
    const shouldShowDiscountedPrice = cart?.id
      ? !!(hasCartDiscount && discountedPrice)
      : !!discountedPrice
    return (
      <Price data-testid='product-card-price'>
        <PriceInfo>
          {pricePrefix && <span>{pricePrefix}&nbsp;</span>}
          {shouldShowDiscountedPrice && (
            <DiscountedPrice>£{discountedPrice}&nbsp;</DiscountedPrice>
          )}
          <BasePrice isDiscounted={shouldShowDiscountedPrice}>
            £{basePrice}
          </BasePrice>
        </PriceInfo>
        <DietaryRequirements dietaryRequirements={dietaryRequirements} />
      </Price>
    )
  }, [
    showFromPrefix,
    minPrice.base,
    minPrice.discounted,
    dietaryRequirements,
    cart?.summary.discount?.code,
    cart?.id
  ])

  /** Title and desc should not exceed 4 lines */
  useEffect(() => {
    if (titleRef.current) {
      const computedStyle = window.getComputedStyle(titleRef.current)
      const fontSize = parseFloat(computedStyle.fontSize)

      /**  Checks if lineHeight is "normal" (browser default),
       * if normal, calculates line height as fontSize * 1.2 (normal line height),
       * Divides total height by line height to get number of lines */
      const lineHeight =
        computedStyle.lineHeight === 'normal'
          ? Math.round(fontSize * 1.2)
          : parseInt(computedStyle.lineHeight, 10)

      const titleHeight = titleRef.current.offsetHeight
      const lines = Math.round(titleHeight / lineHeight)
      setTitleLines(lines)
    }
  }, [productName])

  // product title and description usually takes full length if no image, to avoid overlap
  // with PromoTag make title div width smaller and align promo tag to top of product card
  const hasPromotionNoImage = (product.promotion && !imageUrl) || false

  return (
    <>
      {isModalOpen('cartSwitch') &&
        showCartSwitchModal &&
        cartSwitchModal({
          setShowCartSwitchModal,
          switchCartStore: handleCreateDefaultCart,
          storeName: currentPartnerStore?.name || '',
          cartFulfillmentDatetime: cart?.fulfillment.window.from
        })}
      <RouterAnchor
        hrefLink={productHref}
        search={location.search}
        navigateOptions={{
          state: productLinkState
        }}
      >
        <Container
          data-testid='product-card'
          hasImage={!!imageUrl}
          inStock={inStock}
        >
          <TextContent contentDimensions={contentDimensions}>
            <ProductTitle ref={titleRef} truncateTitleDiv={hasPromotionNoImage}>
              {productName}
            </ProductTitle>
            <ProductDetails>
              {showDescription && (
                <ProductDescription titleLines={titleLines}>
                  {/* Split description at first newline to show only first paragraph */}
                  <SimpleFormat>{description?.split('\n')[0]}</SimpleFormat>
                </ProductDescription>
              )}

              {memoizedPrice}
            </ProductDetails>
          </TextContent>

          <ProductImageContainer
            hasImage={!!imageUrl}
            contentDimensions={contentDimensions}
          >
            {shouldShowPromoTag && product.promotion && (
              <PromoTag promotion={product.promotion} />
            )}
            {isStoreAcceptingOrders && (
              <AddToCartButton
                onClick={quickAddAllowed ? handleAddToCart : handleProductClick}
                buttonState={quickAddAllowed ? buttonState : null}
              />
            )}

            {!inStock && <SoldOutOverlay />}

            {imageUrl && (
              <ProductImage
                src={getImage(116, 116)}
                alt={`Order ${productName} online`}
                loading='lazy'
                isLoaded={isLoaded}
                onLoad={() => setIsLoaded(true)}
                onError={handleImageError}
              />
            )}
          </ProductImageContainer>
        </Container>
      </RouterAnchor>
      <LineBreakWrapper isLast={isLast}>
        <LineBreak />
      </LineBreakWrapper>
    </>
  )
}

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`

const Loading = styled(LoadingIcon)(() => ({
  animation: `${rotate} 1s linear infinite`
}))

const ButtonWrapper = styled.button(
  (): CSSObject => ({
    zIndex: 1,
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    height: '34px',
    width: '34px',
    borderRadius: '50%',
    opacity: 0.8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 0,
    outline: 0,
    fontWeight: 'lighter',
    boxShadow: '0px 2px 4px rgba(98, 98, 98, 0.15)',
    backgroundColor: 'white',
    '&:hover, &:disabled, &:disabled:hover': {
      opacity: 1
    }
  })
)

const Container = styled.div<
  StyledHTMLElement & { inStock: boolean; hasImage: boolean }
>(({ theme, inStock, hasImage }) => ({
  padding: '12px 0',
  borderRadius: theme.borderRadius,
  cursor: 'pointer',
  display: 'grid',
  backgroundColor: 'white',
  gridTemplateColumns: hasImage
    ? 'minmax(0, 1fr) minmax(124px, auto)'
    : 'minmax(0, 1fr) 0',
  gap: '12px',
  [theme.mediaQueries.viewport7]: {
    padding: '12px'
  },
  [theme.mediaQueries.viewport9]: {
    border: '2px solid #FAFAFA',
    '&:hover': {
      boxShadow:
        '0px -3px 12px 1px rgba(0, 0, 0, 0.03), 0px 3px 12px 1px rgba(0, 0, 0, 0.03)'
    }
  },
  // Pseudo element used to overlay parent element
  position: 'relative',
  '&::before': {
    display: inStock ? 'none' : 'grid',
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
    opacity: 0.7,
    borderRadius: theme.borderRadius,
    zIndex: 1
  }
}))

const Price = styled.p(({ theme }) => ({
  fontSize: theme.fontSizes[1],
  margin: 'auto 0 8px',
  [theme.mediaQueries.viewport7]: {
    fontSize: theme.fontSizes[2],
    marginBottom: '8px'
  }
}))

const PriceInfo = styled.span({
  marginRight: '8px'
})

const TextContent = styled.div<
  StyledHTMLElement & {
    contentDimensions?: string
  }
>(({ theme, contentDimensions }) => ({
  width: 'auto',
  display: 'flex',
  flexDirection: 'column',
  height: contentDimensions || '116px',
  [theme.mediaQueries.viewport7]: {
    height: contentDimensions || '126px'
  }
}))

const ProductImageContainer = styled.div<
  StyledHTMLElement & {
    hasImage: boolean
    contentDimensions?: string
  }
>(({ theme, hasImage, contentDimensions }) => ({
  width: contentDimensions || '116px',
  height: contentDimensions || '116px',
  position: 'relative',
  justifySelf: 'end',
  border: hasImage ? '1px solid #EFEFF0' : 'none',
  borderRadius: hasImage ? theme.borderRadius : '0',
  [theme.mediaQueries.viewport7]: {
    width: contentDimensions || '126px',
    height: contentDimensions || '126px'
  }
}))

const ProductImage = styled.img<{ isLoaded: boolean }>(
  ({ theme, isLoaded }) => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    textDecoration: 'none',
    color: 'lightgray',
    borderRadius: theme.borderRadius,
    opacity: 0,
    animation: isLoaded ? `${fadeIn} 0.5s ease-in-out forwards` : 'none'
  })
)

const ProductTitle = styled.h3<
  StyledHTMLElement & {
    truncateTitleDiv: boolean
  }
>(({ theme, truncateTitleDiv }) => ({
  fontWeight: 'bold',
  fontSize: theme.fontSizes[1],
  [theme.mediaQueries.viewport7]: {
    fontSize: theme.fontSizes[2]
  },
  flex: '0 0 auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordWrap: 'break-word',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  marginBottom: '4px',
  width: truncateTitleDiv ? '85%' : 'auto'
}))

const ProductDetails = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flexGrow: 1,
  gap: '4px'
}))

const ProductDescription = styled.div<
  StyledHTMLElement & { titleLines: number }
>(({ theme, titleLines }) => ({
  fontSize: theme.fontSizes[1],
  color: '#595959',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordWrap: 'break-word',
  display: '-webkit-box',
  WebkitLineClamp: titleLines === 1 ? 3 : 2,
  WebkitBoxOrient: 'vertical',
  '& p:first-of-type': {
    marginTop: 0
  }
}))

const BasePrice = styled(Medium)<
  StyledHTMLElement & {
    isDiscounted: boolean
  }
>(({ isDiscounted }) => ({
  color: isDiscounted ? '#59595A' : 'inherit',
  textDecoration: isDiscounted ? 'line-through' : 'none'
}))

const DiscountedPrice = styled(Medium)(() => ({
  color: '#389E0D'
}))

export default ProductCard
